import { useLocation } from 'react-router-dom';

/**
 * Custom React hook to retrieve query parameters from the current URL.
 *
 * @param {string} [query] - An optional specific query parameter to fetch.
 * @returns {Object.<string, string>} - If a `query` is provided, returns an object containing the value of that query parameter.
 *                                      If no `query` is provided, returns an object containing all query parameters as key-value pairs.
 *
 * @example
 * // Example: Retrieving a specific query parameter
 * const { id } = useQueryParams('id');
 * console.log(id); // e.g., '123'
 *
 * @example
 * // Example: Retrieving all query parameters
 * const params = useQueryParams();
 * console.log(params); // e.g., { id: '123', name: 'John' }
 */
export const useQueryParams = (query?: string): { [key: string]: string } => {
  const queryParams = new URLSearchParams(useLocation().search);

  if (query) {
    return { [query]: queryParams.get(query) ?? '' };
  }
  return Object.fromEntries(queryParams);
};
